import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  ExternalLink,
  RefreshCw,
  Save,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Skeleton from '../ui/Skeleton';

const inputStyle = {
  width: '100%',
  background: C.s2,
  border: `1px solid ${C.b1}`,
  borderRadius: 6,
  padding: '9px 11px',
  color: C.t1,
  fontSize: 12,
  fontFamily: FONTS.mono,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 9,
  color: C.t2,
  fontFamily: FONTS.mono,
  marginBottom: 4,
  textTransform: 'uppercase',
};

function parseRegions(value) {
  return String(value || '')
    .split(/[\s,;]+/)
    .map(v => v.trim().toLowerCase())
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i);
}

function formatRegions(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join('\n');
  return value || '';
}

function getStatusMeta(source) {
  if (!source) return { label: 'Not checked', color: C.t2, bg: C.gryBg, Icon: AlertTriangle };
  if (source.scanReady || source.valid) return { label: 'Scan-ready', color: C.grn, bg: C.grnBg, Icon: CheckCircle2 };
  if (source.authenticated || source.roleConfigured || source.accountIdConfigured) return { label: 'Needs permissions', color: C.amb, bg: C.ambBg, Icon: AlertTriangle };
  return { label: 'Not ready', color: C.red, bg: C.redBg, Icon: XCircle };
}

function normalizeOutputKey(key) {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function applyOutputValue(target, key, value) {
  const normalized = normalizeOutputKey(key);
  const raw = Array.isArray(value) ? value.join(',') : String(value || '').trim();
  if (!raw) return;

  if (['accountid', 'awsaccountid'].includes(normalized)) target.accountId = raw;
  if (['rolearn', 'sentinelrolearn', 'awsrolearn'].includes(normalized)) target.roleArn = raw;
  if (['partition', 'awspartition'].includes(normalized)) target.partition = raw;
  if (['regions', 'region', 'awsregions'].includes(normalized)) target.regions = raw;
  if (['rolename', 'sentinelrolename'].includes(normalized)) target.roleName = raw;
  if (['externalid', 'awsexternalid'].includes(normalized)) target.externalId = raw;
}

function parseStackOutputs(text) {
  const raw = String(text || '').trim();
  const parsed = {};
  if (!raw) return parsed;

  const consumeObject = obj => {
    Object.entries(obj || {}).forEach(([key, value]) => applyOutputValue(parsed, key, value));
  };

  try {
    const value = JSON.parse(raw);
    const outputRows = Array.isArray(value)
      ? value
      : Array.isArray(value?.Outputs)
        ? value.Outputs
        : null;

    if (outputRows) {
      outputRows.forEach(row => {
        const key = row?.OutputKey ?? row?.outputKey ?? row?.Key ?? row?.key;
        const outputValue = row?.OutputValue ?? row?.outputValue ?? row?.Value ?? row?.value;
        applyOutputValue(parsed, key, outputValue);
      });
    } else if (value && typeof value === 'object') {
      consumeObject(value);
    }
  } catch {
    raw.split(/\r?\n/).forEach(line => {
      const match = line.match(/^\s*([^:=]+)\s*[:=]\s*(.+?)\s*$/);
      if (match) applyOutputValue(parsed, match[1], match[2]);
    });
  }

  if (parsed.roleArn) {
    const match = parsed.roleArn.match(/^arn:(aws(?:-us-gov)?):iam::(\d{12}):role\/(.+)$/i);
    if (match) {
      parsed.partition ||= match[1];
      parsed.accountId ||= match[2];
      parsed.roleName ||= match[3];
    }
  }

  return parsed;
}

function ResultBanner({ result }) {
  if (!result) return null;
  return (
    <div style={{
      padding: '9px 12px',
      borderRadius: 6,
      fontSize: 11,
      fontFamily: FONTS.mono,
      background: result.ok ? `${C.grn}12` : C.redBg,
      border: `1px solid ${result.ok ? C.grn : C.red}33`,
      color: result.ok ? C.grn : C.red,
      overflowWrap: 'anywhere',
    }}>
      {result.msg}
    </div>
  );
}

function StatusChip({ source }) {
  const state = getStatusMeta(source);
  const Icon = state.Icon;
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      color: state.color,
      background: state.bg,
      border: `1px solid ${state.color}33`,
      borderRadius: 999,
      padding: '4px 8px',
      fontSize: 9,
      fontWeight: 800,
      fontFamily: FONTS.mono,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={13} />
      {state.label}
    </div>
  );
}

function InfoTile({ label, value }) {
  return (
    <div style={{ padding: '8px 10px', background: C.s2, border: `1px solid ${C.b2}`, borderRadius: 6, minWidth: 0 }}>
      <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, overflowWrap: 'anywhere' }}>{value || '-'}</div>
    </div>
  );
}

function PermissionChecks({ validation }) {
  const checks = Array.isArray(validation?.checks) ? validation.checks : [];
  const missing = Array.isArray(validation?.missingPermissions) ? validation.missingPermissions : [];
  if (checks.length === 0 && missing.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {missing.length > 0 && (
        <div style={{ padding: '10px 12px', borderRadius: 7, background: C.redBg, border: `1px solid ${C.red}33` }}>
          <div style={{ fontSize: 10, color: C.red, fontWeight: 800, fontFamily: FONTS.mono, marginBottom: 6 }}>
            Missing permissions ({missing.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {missing.map(item => (
              <div key={item.id || item.service} style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
                <div style={{ color: C.red, fontWeight: 700 }}>{item.service || item.id}</div>
                <div>{Array.isArray(item.requiredActions) ? item.requiredActions.join(', ') : item.requiredActions}</div>
                {item.remediation && <div style={{ color: C.t2 }}>{item.remediation}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
        {checks.map(check => {
          const passed = String(check.status || '').toLowerCase() === 'passed';
          const color = passed ? C.grn : C.red;
          const Icon = passed ? CheckCircle2 : XCircle;
          return (
            <div key={check.id || check.service} style={{ padding: '9px 10px', borderRadius: 7, background: C.s2, border: `1px solid ${color}33`, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <Icon size={14} color={color} />
                <div style={{ fontSize: 10, color, fontWeight: 800, fontFamily: FONTS.mono }}>{check.service || check.id}</div>
              </div>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                {check.message}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AwsOnboardingPanel({
  clientId,
  readOnly = false,
  compact = false,
  embedded = false,
  consentStyle = false,
  configured = false,
  onLoaded,
  onSaved,
  onValidationResult,
}) {
  const api = useApiClient();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [onboarding, setOnboarding] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [partition, setPartition] = useState('aws');
  const [regionsText, setRegionsText] = useState('us-east-1');
  const [stackPaste, setStackPaste] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [validation, setValidation] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedSetupLink, setCopiedSetupLink] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOnboarding(null);
    setValidation(null);
    setActionResult(null);
    setCopied(false);
    setCopiedSetupLink(false);

    if (!clientId) return () => { cancelled = true; };

    setLoading(true);
    setLoadError('');
    api.get(`/clients/${clientId}/aws/onboarding-link`)
      .then(data => {
        if (cancelled) return;
        setOnboarding(data);
        onLoaded?.(data);
        setAccountId(data?.current?.accountId || '');
        setRoleArn(data?.current?.roleArn || '');
        setPartition(data?.current?.partition || 'aws');
        setRegionsText(formatRegions(data?.current?.regions || ['us-east-1']));
      })
      .catch(err => {
        if (!cancelled) setLoadError(err?.message || 'AWS setup could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [api, clientId]);

  const readiness = onboarding?.readiness;
  const statusSource = validation || readiness || (configured ? { scanReady: true } : null);
  const launchUrl = onboarding?.cloudFormationQuickCreateUrl || onboarding?.quickCreateUrl;
  const regions = useMemo(() => parseRegions(regionsText), [regionsText]);
  const canSaveOutputs = Boolean(clientId && !readOnly && (accountId.trim() || roleArn.trim()));

  const copyExternalId = async () => {
    if (!onboarding?.externalId) return;
    await navigator.clipboard.writeText(onboarding.externalId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const copySetupLink = async () => {
    if (!launchUrl) return;
    await navigator.clipboard.writeText(launchUrl);
    setCopiedSetupLink(true);
    setTimeout(() => setCopiedSetupLink(false), 1800);
  };

  const launchAwsSetup = () => {
    if (!launchUrl) return;
    window.open(launchUrl, '_blank', 'noopener,noreferrer');
  };

  const applySavedStackOutputs = saved => {
    setOnboarding(prev => ({
      ...(prev || {}),
      externalId: saved?.externalId || prev?.externalId,
      readiness: saved?.readiness || prev?.readiness,
      current: {
        ...(prev?.current || {}),
        accountId: saved?.accountId || accountId.trim(),
        roleArn: saved?.roleArn || roleArn.trim(),
        partition: saved?.partition || partition,
        regions: saved?.regions || regions,
        environmentConfigured: true,
        onboardingStatus: 'stack-outputs-imported',
      },
    }));
    setAccountId(saved?.accountId || accountId.trim());
    setRoleArn(saved?.roleArn || roleArn.trim());
    setPartition(saved?.partition || partition);
    setRegionsText(formatRegions(saved?.regions || regions));
    onSaved?.(saved);
  };

  const persistStackOutputs = async () => {
    const saved = await api.post(`/clients/${clientId}/aws/stack-outputs`, {
      accountId: accountId.trim() || null,
      roleArn: roleArn.trim() || null,
      partition,
      regions,
    });
    applySavedStackOutputs(saved);
    return saved;
  };

  const applyPastedOutputs = () => {
    const parsed = parseStackOutputs(stackPaste);
    if (parsed.accountId) setAccountId(parsed.accountId);
    if (parsed.roleArn) setRoleArn(parsed.roleArn);
    if (parsed.partition) setPartition(parsed.partition === 'aws-us-gov' ? 'aws-us-gov' : 'aws');
    if (parsed.regions) setRegionsText(formatRegions(parseRegions(parsed.regions)));
    setActionResult({
      ok: Boolean(parsed.accountId || parsed.roleArn || parsed.partition || parsed.regions),
      msg: parsed.accountId || parsed.roleArn || parsed.partition || parsed.regions
        ? 'CloudFormation outputs imported into the fields below.'
        : 'No supported CloudFormation outputs were found.',
    });
  };

  const saveStackOutputs = async () => {
    setSaving(true);
    setActionResult(null);
    try {
      await persistStackOutputs();
      setActionResult({ ok: true, msg: 'AWS role outputs saved. Verify access to confirm scanner permissions.' });
    } catch (err) {
      setActionResult({ ok: false, msg: err?.message || 'Failed to save AWS stack outputs.' });
    } finally {
      setSaving(false);
    }
  };

  const verifyAwsAccess = async () => {
    setVerifying(true);
    setActionResult(null);
    try {
      if (consentStyle && !readOnly) {
        if (!accountId.trim()) {
          const result = {
            provider: 'AWS',
            valid: false,
            scanReady: false,
            error: 'Enter the AWS account ID after the CloudFormation stack completes.',
            testedAt: new Date().toISOString(),
          };
          setValidation(result);
          onValidationResult?.(result);
          setActionResult({ ok: false, msg: result.error });
          return;
        }
        await persistStackOutputs();
      }

      const result = await api.post(`/clients/${clientId}/credentials/validate`, { provider: 'AWS' });
      setValidation(result);
      onValidationResult?.(result);
      setActionResult({
        ok: Boolean(result?.valid || result?.scanReady),
        msg: result?.scanReady
          ? 'AWS access verified. The client is scan-ready.'
          : result?.authenticated
            ? result?.error || 'AWS authenticated, but scanner permissions are incomplete.'
            : result?.error || 'AWS access could not be verified.',
      });
    } catch (err) {
      const result = {
        provider: 'AWS',
        valid: false,
        scanReady: false,
        error: err?.message || 'AWS verification failed.',
        testedAt: new Date().toISOString(),
      };
      setValidation(result);
      onValidationResult?.(result);
      setActionResult({ ok: false, msg: result.error });
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    const loadingContent = <Skeleton height={compact ? 160 : 220} />;
    return embedded
      ? <div style={{ padding: compact ? 14 : 18, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s2 }}>{loadingContent}</div>
      : <Card style={{ padding: compact ? 18 : 22 }}>{loadingContent}</Card>;
  }

  const content = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <ShieldCheck size={18} color={C.sky} />
            <div style={{ fontSize: 14, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans }}>AWS Role Setup</div>
          </div>
          <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
            Launch AWS CloudFormation, enter the AWS account ID after the stack completes, then verify scanner access.
          </div>
        </div>
        <StatusChip source={statusSource} />
      </div>

      {loadError && (
        <div style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 6, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 11, fontFamily: FONTS.mono }}>
          {loadError}
        </div>
      )}

      {!loadError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {onboarding?.sentinelPrincipalArnConfigured === false && (
            <div style={{ padding: '9px 12px', borderRadius: 7, background: C.ambBg, border: `1px solid ${C.amb}33`, color: C.amb, fontSize: 10, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
              Quick Create is unavailable until the backend API is configured with the Sentinel AWS principal ARN. The template URL is still available for manual review.
            </div>
          )}
          {onboarding?.sentinelPrincipalArnConfigured !== false && onboarding?.quickCreateUnavailableReason && (
            <div style={{ padding: '9px 12px', borderRadius: 7, background: C.ambBg, border: `1px solid ${C.amb}33`, color: C.amb, fontSize: 10, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
              Quick Create is unavailable: {onboarding.quickCreateUnavailableReason}
            </div>
          )}

          <div style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.sky}33`, background: `${C.sky}08` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 220, flex: '1 1 280px' }}>
                <div style={{ fontSize: 11, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 4 }}>AWS setup link</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
                  Opens AWS CloudFormation with the required role values already filled in.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="pri" onClick={launchAwsSetup} disabled={readOnly || !launchUrl}>
                  <ExternalLink size={14} />
                  Launch AWS Setup
                </Button>
                <Button variant="sec" onClick={copySetupLink} disabled={!launchUrl}>
                  <Clipboard size={14} />
                  {copiedSetupLink ? 'Copied' : 'Copy Setup Link'}
                </Button>
                {!consentStyle && onboarding?.templateUrl && (
                  <Button variant="sec" onClick={() => window.open(onboarding.templateUrl, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink size={14} />
                    Template
                  </Button>
                )}
              </div>
            </div>
          </div>

          <details style={{ padding: '10px 12px', borderRadius: 7, border: `1px solid ${C.b1}`, background: C.s2 }}>
            <summary style={{ cursor: 'pointer', color: C.t2, fontSize: 10, fontFamily: FONTS.mono, fontWeight: 800 }}>
              Advanced setup values
            </summary>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                <InfoTile label="Expected role" value={onboarding?.expectedRoleName} />
                <InfoTile label="Role ARN pattern" value={onboarding?.expectedRoleArnPattern} />
                <InfoTile label="Stack name" value={onboarding?.stackName} />
                <InfoTile label="Auth mode" value={readiness?.preferredAuthMode || 'assume-role'} />
              </div>
              <div>
                <div style={labelStyle}>ExternalId included in launch link</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ ...inputStyle, background: C.s1, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {onboarding?.externalId || '-'}
                  </div>
                  <Button variant="sec" onClick={copyExternalId} disabled={!onboarding?.externalId} title="Copy ExternalId">
                    <Clipboard size={14} />
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            </div>
          </details>

          {consentStyle ? (
            <div style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s1 }}>
              <div style={{ fontSize: 11, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 5 }}>Verify setup</div>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 10 }}>
                After AWS shows the stack is complete, enter the AWS account ID and verify access.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'minmax(180px, 260px) auto', gap: 8, alignItems: 'end' }}>
                <div>
                  <div style={labelStyle}>AWS Account ID</div>
                  <input value={accountId} onChange={e => setAccountId(e.target.value)} placeholder="123456789012" readOnly={readOnly} style={inputStyle} />
                </div>
                <Button variant="pri" onClick={verifyAwsAccess} disabled={!clientId || verifying || saving}>
                  <RefreshCw size={14} />
                  {verifying ? 'Verifying...' : statusSource?.scanReady || statusSource?.valid ? 'Verify Again' : 'Verify AWS Access'}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div>
                <div style={labelStyle}>Account ID</div>
                <input value={accountId} onChange={e => setAccountId(e.target.value)} placeholder="123456789012" readOnly={readOnly} style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Partition</div>
                <select value={partition} onChange={e => setPartition(e.target.value)} disabled={readOnly} style={inputStyle}>
                  <option value="aws">Commercial</option>
                  <option value="aws-us-gov">GovCloud</option>
                </select>
              </div>
              <div style={{ gridColumn: compact ? 'auto' : '1 / span 2' }}>
                <div style={labelStyle}>Role ARN</div>
                <input value={roleArn} onChange={e => setRoleArn(e.target.value)} placeholder={onboarding?.expectedRoleArnPattern || 'arn:aws:iam::123456789012:role/SentinelCommandSecurityAudit'} readOnly={readOnly} style={inputStyle} />
              </div>
              <div style={{ gridColumn: compact ? 'auto' : '1 / span 2' }}>
                <div style={labelStyle}>Regions</div>
                <textarea value={regionsText} onChange={e => setRegionsText(e.target.value)} rows={3} readOnly={readOnly} placeholder={'us-east-1\nus-east-2'} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>
          )}

          {!readOnly && !consentStyle && (
            <div style={{ padding: '10px 12px', borderRadius: 7, border: `1px solid ${C.b1}`, background: C.s1 }}>
              <div style={labelStyle}>Import CloudFormation outputs</div>
              <textarea
                value={stackPaste}
                onChange={e => setStackPaste(e.target.value)}
                rows={compact ? 3 : 4}
                placeholder={'RoleArn=arn:aws:iam::123456789012:role/...\nAccountId=123456789012\nPartition=aws'}
                style={{ ...inputStyle, resize: 'vertical', marginBottom: 8 }}
              />
              <Button variant="sec" onClick={applyPastedOutputs} disabled={!stackPaste.trim()}>
                Import Outputs
              </Button>
            </div>
          )}

          <ResultBanner result={actionResult} />

          {readiness?.missing?.length > 0 && !validation && !consentStyle && (
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
              Missing: {readiness.missing.join(', ')}
            </div>
          )}

          <PermissionChecks validation={validation} />

          {!consentStyle && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              {!readOnly && (
                <Button variant="sec" onClick={saveStackOutputs} disabled={saving || !canSaveOutputs}>
                  <Save size={14} />
                  {saving ? 'Saving...' : 'Save AWS Outputs'}
                </Button>
              )}
              <Button variant="pri" onClick={verifyAwsAccess} disabled={!clientId || verifying || saving}>
                <RefreshCw size={14} />
                {verifying ? 'Verifying...' : 'Verify AWS Access'}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  return embedded
    ? <div style={{ padding: compact ? 14 : 18, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s2 }}>{content}</div>
    : <Card style={{ padding: compact ? 18 : 22 }}>{content}</Card>;
}
